import type { IDBPDatabase } from 'idb';
import { openDB, unwrap } from 'idb';

import { type Schema as PMSchema } from '@atlaskit/editor-prosemirror/model';
import { Step } from '@atlaskit/editor-prosemirror/transform-override';
import { AnalyticsStep } from '@atlaskit/adf-schema/steps/analytics';
import { LinkMetaStep } from '@atlaskit/adf-schema/steps/link-meta-step';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { getDocBeforeUnconfirmedSteps, getCollabState } from '@atlaskit/prosemirror-collab';
import { type Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { fg } from '@confluence/feature-gating';

import { editorOfflineStoragePluginKey } from './pm-plugins/main.ts';
import { SynchronisationLock, type LockPurpose } from './syncLock.ts';
import type { EditorOfflineStoragePlugin } from './editorOfflineStoragePluginType.ts';
import { fireOfflineStorageAnalyticsEvent, parseErrorMessage } from './analytics.ts';
import {
	runBackgroundCleanup,
	shouldRunBackgroundCleanup,
	markBackgroundCleanupAttempted,
} from './databaseCleanup.ts';
import {
	OFFLINE_STEPS_STORE_NAME,
	OFFLINE_META_STORE_NAME,
	OFFLINE_STEPS_DATABASE_NAME_PREFIX,
	OFFLINE_STEPS_DATABASE_VERSION,
	type StepsDBSchema,
	type VersionInsertionBackup,
} from './database-schema.ts';

const SYNC_LOCK_TIMEOUT_MS = 20000;
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export type GetDataResponse = {
	steps: Step[];
	docJSON: JSONDocNode | null;
	version: number | null;
};

export class StepsDatabase {
	private dbPromise: Promise<IDBPDatabase<StepsDBSchema>> | undefined;
	private contentId: string | undefined;
	private syncLock = new SynchronisationLock();
	private api: PublicPluginAPI<[EditorOfflineStoragePlugin]> | undefined;
	private initAttempted = false;
	private indexedDBAvailable: boolean | undefined; // Used to cache the result of the IndexedDB validation.

	private onError(_id: string, error: unknown, disabledReason?: string) {
		// Indicate the steps database is not healthy.
		this.api?.core.actions.execute(({ tr }: { tr: Transaction }) =>
			tr.setMeta(editorOfflineStoragePluginKey, {
				disabled: disabledReason ?? 'stepsDatabaseError',
			}),
		);

		fireOfflineStorageAnalyticsEvent({
			api: this.api,
			action: ACTION.OFFLINE_STORAGE_STEPS_DATABASE_ERROR,
			errorMessage: parseErrorMessage(error),
			errorSource: _id,
			errorCause: {
				contentIdPresent: !!this.contentId,
				stepsDatabaseInitAttempted: this.initAttempted,
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				isSSR: !!process.env.REACT_SSR,
				isIndexedDBAvailable: this.indexedDBAvailable,
			},
		});
	}
	/**
	 * The name of the database for the current contentId
	 */
	public get dbName(): string | undefined {
		if (!this.contentId) {
			return undefined;
		}
		return `${OFFLINE_STEPS_DATABASE_NAME_PREFIX}${this.contentId}`;
	}

	public get hasLock(): boolean {
		return this.syncLock.hasLock;
	}

	public get lockPurpose(): LockPurpose | undefined {
		return this.syncLock.lockPurpose;
	}

	public isLocked(): Promise<boolean> {
		return this.syncLock.isLocked();
	}

	/**
	 * Releases the current sync lock. Returns a promise to check if the lock has been released
	 * at this point which can be useful if you want to do other operations (ie. update the DB)
	 * based on the lock status and need it to be freed.
	 *
	 * @returns boolean: The result if the sync is still locked.
	 */
	public async releaseSyncLock(): Promise<boolean> {
		this.syncLock.releaseLock?.();
		return this.syncLock.isLocked();
	}

	/**
	 * Attempts to acquire a synchronization lock for the database.
	 *
	 * @param callback - Optional callback function that is called when the lock is acquired or times out.
	 *                  The callback receives a boolean indicating if the lock was granted.
	 * @param waitUntilAvailable - If true, waits until the lock becomes available. If false, returns immediately.
	 * @param timeoutAfterMS - Optional timeout in milliseconds. If provided and waitUntilAvailable is true,
	 *                        the callback will be called with false if the lock isn't acquired within this time.
	 *                        When platform_editor_offline_editing_web_ga_fixes is cleaned up, this parameter can be removed.
	 * @returns A Promise if waiting for the lock, void if not waiting.
	 */
	public acquireSyncLock(
		callback?: (granted?: boolean) => Promise<void>,
		waitUntilAvailable = false,
		purpose?: LockPurpose,
		timeoutAfterMS?: number,
	): Promise<unknown> | void {
		const lockPromise = this.syncLock
			.acquireLock(callback, waitUntilAvailable, purpose)
			.catch((e) => {
				this.onError('acquireSyncLock()', e);
				// Ensure callback is called with false on error
				return callback?.(false);
			});

		// If we're not waiting for the lock, fire-and-forget
		if (!waitUntilAvailable) {
			void lockPromise;
			return;
		}

		const effectiveTimeout = fg('platform_editor_offline_editing_web_ga_fixes')
			? SYNC_LOCK_TIMEOUT_MS
			: timeoutAfterMS;

		// If we have a timeout, race the lock acquisition with a timeout. Redundant check when platform_editor_offline_editing_web_ga_fixes is cleaned up.
		if (effectiveTimeout) {
			const timeoutPromise = new Promise<void>((resolve) => {
				setTimeout(async () => {
					// Callback invoked with granted = false, failed attempt to acquire lock.
					await callback?.(false);
					resolve();
				}, effectiveTimeout);
			});

			return Promise.race([lockPromise, timeoutPromise]);
		}

		return lockPromise;
	}

	/**
	 * Initialize the class for the given contentId.
	 * Can be re-initialized with a different contentId, which will close an existing connection.
	 * Does not open the database connection.
	 * @param contentId The unique page contentId
	 * @returns true if initialized successfully, false if indexedDB is not supported.
	 */
	async init(
		contentId: string,
		api: PublicPluginAPI<[EditorOfflineStoragePlugin]> | undefined,
	): Promise<boolean> {
		this.initAttempted = true;
		this.api = api;

		if (window?.indexedDB === undefined) {
			this.onError('init()', new Error('indexedDB is not defined'));
			return false;
		}

		if (this.contentId === contentId && this.dbPromise) {
			return true;
		}

		this.contentId = contentId;
		if (this.dbName) {
			this.syncLock.init(this.dbName);
		}
		return true;
	}

	/**
	 * Validates IndexedDB access by attempting a test write.
	 * @returns Promise<boolean> - true if validation succeeds, false if it fails
	 */
	public async validateIndexedDBAccess(): Promise<boolean> {
		// Check the cached indexedDBAvailable to avoid validating multiple times.
		if (this.indexedDBAvailable !== undefined) {
			return this.indexedDBAvailable ?? true;
		}

		try {
			if (window?.indexedDB === undefined) {
				throw new Error('indexedDB is not defined');
			}

			const testDbName = 'editor:offline-storage-access-test';
			const testDb = await openDB(testDbName, 1, {
				upgrade(database) {
					database.createObjectStore('test', { autoIncrement: true });
				},
			});

			// Try a test write
			const tx = testDb.transaction('test', 'readwrite');
			const store = tx.objectStore('test');
			await store.add({ test: 'test' });
			await tx.done;

			// Clean up
			testDb.close();
			window.indexedDB.deleteDatabase(testDbName);
			this.indexedDBAvailable = true;
			return true;
		} catch (error) {
			this.onError('validateIndexedDBAccess()', error, 'indexedDBAccessError');
			this.indexedDBAvailable = false;
			return false;
		}
	}

	/**
	 * Gets or creates a database connection for the current contentId.
	 * If a connection exists, validates it is still usable before returning.
	 * If validation fails or no connection exists, creates and returns a new connection.
	 *
	 * @throws {Error} When dbName is undefined (not initialized – missing contentId, init not called, or in SSR mode)
	 * @returns A promise that resolves to a valid database connection
	 */
	private async openDB(): Promise<IDBPDatabase<StepsDBSchema>> {
		if (this.dbName === undefined) {
			throw new Error(
				'StepsDatabase not initialized. Possibly missing contentId, init hasnt been called or rendering in SSR mode.',
			);
		}

		// If we have a cached connection, we validate it with a quick read transaction.
		// If this transaction fails, we close the connection and create a new one.
		if (this.dbPromise) {
			try {
				const db = await this.dbPromise;

				await db
					.transaction(OFFLINE_META_STORE_NAME, 'readonly')
					.objectStore(OFFLINE_META_STORE_NAME)
					.count();

				// Returning the valid cached database connection.
				return this.dbPromise;
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (_error) {
				// Cleanup before creating a new connection.
				await this.closeDB();
			}
		}

		this.dbPromise = openDB<StepsDBSchema>(this.dbName, OFFLINE_STEPS_DATABASE_VERSION, {
			upgrade(database) {
				if (!database.objectStoreNames.contains(OFFLINE_STEPS_STORE_NAME)) {
					database.createObjectStore(OFFLINE_STEPS_STORE_NAME, {
						keyPath: 'id',
						autoIncrement: true,
					});
				}
				if (!database.objectStoreNames.contains(OFFLINE_META_STORE_NAME)) {
					database.createObjectStore(OFFLINE_META_STORE_NAME, {});
				}
			},
			terminated: () => {
				this.dbPromise = undefined;
			},
			blocking: (_currentVersion, _blockedVersion, event) => {
				// Close the database connection when version changes or database is deleted
				// This is important for Firefox compatibility when deleting databases. See HOT-12395 for more details.
				if (event?.target) {
					// event.target is the database object that the versionchange event was fired on
					(event.target as IDBPDatabase<StepsDBSchema>).close();
				}
			},
		});

		return this.dbPromise;
	}

	private sanitizeStep(step: Step): any {
		if (step instanceof AnalyticsStep || step instanceof LinkMetaStep) {
			return undefined;
		}
		return step.toJSON();
	}

	/**
	 * Update the steps, doc and version in the database if the stored steps are different.
	 * It does a full clear and replace with the new steps.
	 * @param version The collab version for creating unique key/value pairs in the db
	 * @param steps The steps to store in the database
	 * @returns true if the steps were updated, false if the steps were the same as the stored steps
	 */
	async updateIfChanged(
		version: number,
		baseDocJSON: JSONDocNode,
		steps: readonly Step[],
	): Promise<boolean> {
		try {
			const isLocked = (await this.syncLock.isLocked()) && !this.hasLock;
			if (isLocked) {
				return false;
			}
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readwrite');
			const stepsStore = unwrap(tx.objectStore(OFFLINE_STEPS_STORE_NAME));
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			// We breakout of idb here. The reason is it ensures we have a single promise
			// for `updateSteps` and ensures `fake-indexeddb` works as intended here.
			// This allows us to test as close as possible to real indexeddb
			const updateSteps = async () => {
				await new Promise((resolve, reject) => {
					const localSteps = steps.map(this.sanitizeStep).filter((step) => !!step);

					const request = stepsStore.clear();
					request.onsuccess = () => {
						// Store serialized steps in the database
						let index = 0;
						const addNextStep = () => {
							if (index < localSteps.length) {
								const serializedStep = localSteps[index];
								const addRequest = stepsStore.add({ step: serializedStep });

								addRequest.onsuccess = () => {
									index++;
									addNextStep();
								};
								addRequest.onerror = (event: Event) => {
									reject((event.target as IDBRequest)?.error);
								};
							} else {
								resolve(undefined);
							}
						};
						addNextStep();
					};
					request.onerror = (event: Event) => {
						reject((event.target as IDBRequest)?.error);
					};
				});
			};

			await Promise.all([
				updateSteps(),
				// Store the doc, version, and lastUpdated timestamp
				docStore.put({ doc: baseDocJSON, version, lastUpdated: Date.now() }, 1),
			]);

			await tx.done;
			return true;
		} catch (e) {
			this.onError('updateIfChanged()', e);
			throw e;
		}
	}

	/**
	 * Update the stored docJSON and version in the database.
	 * This can be called by the leader after applying steps to save the post-apply state for followers.
	 * Extracts the confirmed document state and collaboration version from the editor view.
	 * @param editorView The editor view to extract document state and version from
	 * @returns Promise<boolean> - true if updated successfully, throws on error
	 */
	async updateDocAndVersion(editorView: EditorView): Promise<boolean> {
		try {
			const newVersion = getCollabState(editorView.state)?.version;

			if (newVersion === undefined) {
				return false;
			}

			const db = await this.openDB();
			const tx = db.transaction(OFFLINE_META_STORE_NAME, 'readwrite');
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);
			const transformer = new JSONTransformer();
			// We use getDocBeforeUnconfirmedSteps to ensure we get the latest _confirmed_ document, as some
			// ... unconfirmed steps may have come through while clearing the offline steps and updating the docJSON.
			const newDocJSON = transformer.encode(getDocBeforeUnconfirmedSteps(editorView.state));

			// Update doc, version, and lastUpdated timestamp
			const updatedData = {
				doc: newDocJSON,
				version: newVersion,
				lastUpdated: Date.now(),
			};
			await docStore.put(updatedData, 1);

			await tx.done;
			return true;
		} catch (e) {
			this.onError('updateDocAndVersion()', e);
			throw e;
		}
	}

	/**
	 * Retrieve the steps, doc and version from the database.
	 * Will open a new database connection, if one is not already open.
	 * @param pmSchema The current ProseMirror schema
	 * @returns The steps stored in the database
	 */
	async getData(pmSchema: PMSchema): Promise<GetDataResponse> {
		try {
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readonly');
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			// Collect all steps
			const collectSteps = async () => {
				const steps = await stepsStore.getAll();
				return steps.map((stepJson: { step: object }) => Step.fromJSON(pmSchema, stepJson.step));
			};

			const [storedSteps, data] = await Promise.all([collectSteps(), docStore.get(1)]);

			await tx.done;
			return data === undefined
				? { steps: storedSteps, docJSON: null, version: null }
				: { steps: storedSteps, docJSON: data.doc, version: data.version };
		} catch (e) {
			this.onError('getData()', e);
			throw e;
		}
	}

	/**
	 * Clear all steps, and stored doc from the database
	 * Will open a new database connection, if one is not already open.
	 */
	async clear(all?: boolean): Promise<boolean> {
		try {
			const isLocked = (await this.syncLock.isLocked()) && !this.hasLock;
			if (isLocked) {
				return false;
			}
			const db = await this.openDB();
			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readwrite');
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			if (all) {
				await Promise.all([stepsStore.clear(), docStore.clear()]);
			} else {
				await stepsStore.clear();
			}
			await tx.done;
			return true;
		} catch (e) {
			this.onError('clear()', e);
			throw e;
		}
	}

	/**
	 * Check and clear expired data from the current document's database.
	 * Also triggers background cleanup of other databases (throttled to once per 24 hours).
	 * This should be called after init() but before attempting to restore steps.
	 */
	async checkAndClearExpiredData(): Promise<void> {
		// Check and clear current document's expired data
		await this.checkCurrentDocumentExpiry();

		// Run background cleanup of other document's offline databases (non-blocking, throttled)
		if (shouldRunBackgroundCleanup()) {
			// Mark attempted immediately to prevent race condition if multiple tabs open simultaneously
			markBackgroundCleanupAttempted();

			void runBackgroundCleanup(this.dbName, this.api);
		}
	}

	/**
	 * Check if the current document's offline data has expired and clear it if needed.
	 * Uses instance methods (this.openDB(), this.clear()) rather than deleting the database entirely.
	 * This preserves the database structure and connection since the current document may be
	 * actively written to (e.g. user goes offline). Background cleanup of other databases
	 * can safely delete them entirely as they have no active StepsDatabase instance.
	 *
	 * Backwards compatibility: If data exists but no lastUpdated, adds timestamp = now (30-day window starts from today).
	 * Expiry: If lastUpdated > 30 days, clears all data via clear(true)
	 */
	private async checkCurrentDocumentExpiry(): Promise<void> {
		try {
			const db = await this.openDB();

			// Early exit if the object stores don't exist yet.
			// This is expected for users who haven't used offline storage before.
			if (
				!db.objectStoreNames.contains(OFFLINE_META_STORE_NAME) ||
				!db.objectStoreNames.contains(OFFLINE_STEPS_STORE_NAME)
			) {
				return;
			}

			const tx = db.transaction([OFFLINE_STEPS_STORE_NAME, OFFLINE_META_STORE_NAME], 'readwrite');
			const metaStore = tx.objectStore(OFFLINE_META_STORE_NAME);
			const stepsStore = tx.objectStore(OFFLINE_STEPS_STORE_NAME);

			const [metaData, stepsCount] = await Promise.all([metaStore.get(1), stepsStore.count()]);

			if (!metaData) {
				await tx.done;
				return;
			}

			const now = Date.now();

			// Backwards compatibility: If data exists but no lastUpdated timestamp, add current timestamp
			// This starts the 30-day window from today for existing data
			if (!metaData.lastUpdated) {
				const updatedData = { ...metaData, lastUpdated: now };
				await metaStore.put(updatedData, 1);
				await tx.done;
				return;
			}

			// Check if data has expired (>30 days old) - single timestamp for all data
			if (now - metaData.lastUpdated > THIRTY_DAYS_MS) {
				await tx.done;

				// Gather analytics metadata
				const hasBackup = !!metaData.versionInsertionBackup;
				const daysExpired = Math.floor((now - metaData.lastUpdated) / (24 * 60 * 60 * 1000));

				if (this.contentId) {
					fireOfflineStorageAnalyticsEvent({
						api: this.api,
						action: 'OFFLINE_EDITING_DATA_EXPIRY_CLEANUP',
						contentId: this.contentId,
						daysExpired,
						stepsCount,
						hasBackup,
					});
				}

				await this.clear(true);
				return;
			}

			await tx.done;
		} catch (e) {
			this.onError('checkCurrentDocumentExpiry()', e);
		}
	}

	async closeDB(releaseSyncLock = false): Promise<void> {
		try {
			if (this.dbPromise) {
				const db = await this.dbPromise;
				db.close();
				this.dbPromise = undefined;
				if (releaseSyncLock) {
					this.syncLock.releaseLock?.();
				}
			}
		} catch (e) {
			this.onError('closeDB()', e);
			throw e;
		}
	}

	/**
	 * Store a version insertion backup for later retry
	 * @param adfContent Stringified ADF content that failed to be inserted
	 * @returns Promise<boolean> - true if stored successfully
	 */
	async storeVersionInsertionBackup(adfContent: string): Promise<boolean> {
		if (!fg('platform_editor_offline_editing_web_ga_fixes')) {
			return false;
		}

		try {
			const db = await this.openDB();
			const tx = db.transaction(OFFLINE_META_STORE_NAME, 'readwrite');
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			const existingData = await docStore.get(1);

			// Only store backup if meta store already exists with doc and version
			// If it doesn't exist, there's no document to store a backup for
			if (!existingData || !existingData.doc || existingData.version === undefined) {
				await tx.done;
				return false;
			}

			// Create the backup object (no timestamp - uses shared lastUpdated)
			const versionInsertionBackup: VersionInsertionBackup = {
				adfContent,
			};

			// Preserve all existing data and update both versionInsertionBackup and lastUpdated
			const updatedData: StepsDBSchema['meta']['value'] = {
				...existingData,
				versionInsertionBackup,
				lastUpdated: Date.now(),
			};
			await docStore.put(updatedData, 1);

			await tx.done;
			return true;
		} catch (e) {
			this.onError('storeVersionInsertionBackup()', e);
			return false;
		}
	}

	/**
	 * Get the stored version insertion backup if one exists
	 * @returns Promise<VersionInsertionBackup | null>
	 */
	async getVersionInsertionBackup(): Promise<VersionInsertionBackup | null> {
		if (!fg('platform_editor_offline_editing_web_ga_fixes')) {
			return null;
		}

		try {
			const db = await this.openDB();

			// Early exit if the meta store doesn't exist yet.
			// This is expected for users who haven't used offline storage before.
			if (!db.objectStoreNames.contains(OFFLINE_META_STORE_NAME)) {
				return null;
			}

			const tx = db.transaction(OFFLINE_META_STORE_NAME, 'readonly');
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			const data = await docStore.get(1);
			await tx.done;

			return data?.versionInsertionBackup ?? null;
		} catch (e) {
			this.onError('getVersionInsertionBackup()', e);
			return null;
		}
	}

	/**
	 * Clear the stored version insertion backup and update lastUpdated timestamp
	 * @returns Promise<boolean> - true if cleared successfully
	 */
	async clearVersionInsertionBackup(): Promise<boolean> {
		if (!fg('platform_editor_offline_editing_web_ga_fixes')) {
			return false;
		}

		try {
			const db = await this.openDB();
			const tx = db.transaction(OFFLINE_META_STORE_NAME, 'readwrite');
			const docStore = tx.objectStore(OFFLINE_META_STORE_NAME);

			const existingData = await docStore.get(1);

			// If no data exists or no error field exists, nothing to clear
			if (!existingData || !existingData.versionInsertionBackup) {
				await tx.done;
				return true;
			}

			// Remove the versionInsertionBackup field and update timestamp
			const { versionInsertionBackup: _versionInsertionBackup, ...dataWithoutBackup } =
				existingData;
			await docStore.put(
				{
					...dataWithoutBackup,
					lastUpdated: Date.now(),
				},
				1,
			);

			await tx.done;
			return true;
		} catch (e) {
			this.onError('clearVersionInsertionBackup()', e);
			return false;
		}
	}
}
